// DMNotifier.js — notifieur GLOBAL des messages privés. Monté app-wide : rejoint les canaux DM de tous
// mes amis au login, écoute les messages, et sur un nouveau MP (hors conversation ouverte) affiche un
// TOAST éphémère (bas-gauche) + incrémente le compteur de non-lus (dmNotify). Clic → onglet Messages.
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from '../theme';
import { useAuth } from '../context/AuthContext';
import { parseContent, isControl } from './ChatMessageBody';
import { bumpUnread, isConversationOpen, setUnread, lastReadTs } from '../services/dmNotify';
import { openChatWindow } from '../services/chatWindows';

const slideIn = keyframes`from{transform:translateY(12px);opacity:0}to{transform:translateY(0);opacity:1}`;
const Wrap = styled.div`position:fixed;left:1rem;bottom:5rem;z-index:4000;display:flex;flex-direction:column;gap:.5rem;`;
const Toast = styled.button`display:flex;align-items:center;gap:.6rem;background:${theme.colors.bgCard};text-align:left;cursor:pointer;
  border:1px solid ${theme.colors.borderHover};border-radius:${theme.radii.md};padding:.6rem .9rem;
  box-shadow:${theme.shadows.card};font-size:.85rem;color:${theme.colors.textPrimary};animation:${slideIn} .25s ease;max-width:300px;
  .ico{font-size:1.1rem;flex-shrink:0;}`;

export default function DMNotifier() {
  const { auth, isLoggedIn } = useAuth();
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    if (!isLoggedIn || !auth.enabled || (auth.session && auth.session._localGuest)) return undefined;
    let alive = true;
    const meId = (auth.session && auth.session.user_id) || null;
    const dmChannels = {}; // channelId -> { fid, name }

    // Rejoint le canal DM (type 2) de chaque ami pour recevoir ses MP même hors de la conversation.
    const joinAll = async () => {
      const fr = await auth.listFriends(0);
      await Promise.all((fr || []).map(async (f) => {
        const fid = f.user && (f.user.id || f.user.user_id); if (!fid) return;
        const ch = await auth.joinChannel(fid, 2, true, true);   // hidden=true → écoute passive SANS notif « wants to chat »
        if (ch && alive) {
          dmChannels[ch.id] = { fid, name: (f.user.display_name || f.user.username || 'Un ami') };
          // Non-lus INITIAUX (MP arrivés hors-ligne) : compte les messages de l'ami plus récents que le dernier lu.
          try {
            const msgs = await auth.listChannelMessages(ch.id, 50);
            const since = lastReadTs(fid);
            let n = 0;
            (msgs || []).forEach((m) => {
              if (m.sender_id === meId) return;
              const t = m.create_time ? new Date(m.create_time).getTime() : 0;
              if (t <= since) return;
              const c = parseContent(m.content);
              if (!isControl(c) && (c.text || c.sticker || c.gif || c.audio)) n++;
            });
            if (alive && !isConversationOpen(fid)) setUnread(fid, n);
          } catch { /* pas d'historique → 0 */ }
        }
      }));
    };
    joinAll();

    const seen = new Set();          // dédup par message_id → anti double-livraison (course de sockets à la reconnexion)
    const lastToastAt = {};          // fid -> dernier toast affiché (throttle)
    const TOAST_COOLDOWN = 120000;   // 2 min : on ne re-notifie pas à CHAQUE message. « De temps en temps » pour
                                     // raccrocher à la discussion ; en pleine conversation (fenêtre ouverte) → aucun toast.
    const unsub = auth.onChatMessage((m) => {
      const info = dmChannels[m.channel_id];
      if (!info || m.sender_id === meId) return;      // pas un DM suivi, ou mon propre message
      if (m.message_id) {                              // DÉDUP : ignore une 2e livraison du même message
        if (seen.has(m.message_id)) return;
        seen.add(m.message_id); if (seen.size > 500) seen.clear();
      }
      const c = parseContent(m.content);
      if (isControl(c) || (!c.text && !c.sticker && !c.gif && !c.audio)) return;
      bumpUnread(info.fid);                            // compteur non-lus (ignoré si conv ouverte)
      if (isConversationOpen(info.fid)) return;        // conversation ouverte (fenêtre flottante ou espace membre) → pas de toast
      const now = Date.now();
      if (now - (lastToastAt[info.fid] || 0) < TOAST_COOLDOWN) return;  // throttle → pas un toast à chaque message
      lastToastAt[info.fid] = now;
      const tid = info.fid + ':' + now;
      setToasts((prev) => [...prev.slice(-3), { id: tid, fid: info.fid, name: info.name }]);
      setTimeout(() => { if (alive) setToasts((prev) => prev.filter((t) => t.id !== tid)); }, 6000);
    });
    // Reconnexion socket (bfcache/veille) → on rejoint à nouveau les canaux DM.
    const offRe = auth.onReconnect(joinAll);

    return () => { alive = false; if (unsub) unsub(); if (offRe) offRe(); };
  }, [auth, isLoggedIn]);

  if (!toasts.length) return null;
  return (
    <Wrap>
      {toasts.map((t) => (
        <Toast key={t.id} onClick={() => { setToasts((p) => p.filter((x) => x.id !== t.id)); openChatWindow({ user_id: t.fid, display_name: t.name }); }}>
          <span className="ico">💬</span>
          <span><b>{t.name}</b> t'a écrit un message privé</span>
        </Toast>
      ))}
    </Wrap>
  );
}
